import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LucideIcon } from "lucide-react";

interface FormFieldProps {
  id: string;
  label: string;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  isValid?: boolean;
  formSubmitted?: boolean;
  errorMessage?: string;
  icon?: React.ReactNode;
  className?: string;
  isTextArea?: boolean;
  rows?: number;
  onChangeTextArea?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
}

const FormField = ({
  id,
  label,
  value,
  onChange,
  onChangeTextArea,
  placeholder = "",
  type = "text",
  required = false,
  isValid = true,
  formSubmitted = false,
  errorMessage = "Este campo es requerido",
  icon,
  className = "",
  isTextArea = false,
  rows = 4,
  disabled = false
}: FormFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={`relative group mb-6 ${className}`}>
      <Label 
        htmlFor={id} 
        className={`block text-xs xl:text-sm font-medium mb-3 transition-colors duration-200 ${
          !isValid && formSubmitted 
            ? "text-red-500" 
            : isFocused 
              ? "text-blue-600" 
              : "text-gray-700"
        }`}
      >
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      {isTextArea ? (
        <div className="relative">
          <textarea
            id={id}
            value={value}
            onChange={onChangeTextArea} 
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            rows={rows}
            className={`w-full rounded-lg border ${
              !isValid && formSubmitted 
                ? "border-red-500 focus-visible:ring-red-500" 
                : isFocused 
                  ? "border-blue-500 focus-visible:ring-blue-500" 
                  : "border-gray-300"
            } shadow-sm p-4 text-sm placeholder:text-sm placeholder:text-gray-400 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-50 group-hover:border-gray-400`}
            placeholder={placeholder}
          />
        </div>
      ) : (
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <div className={`h-5 w-5 ${
                !isValid && formSubmitted 
                  ? "text-red-500" 
                  : isFocused 
                    ? "text-blue-600" 
                    : "text-gray-400"
              }`}>
                {icon}
              </div>
            </div>
          )}
          <Input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            className={`w-full ${
              icon ? "pl-12" : "px-4"
            } rounded-lg border ${
              !isValid && formSubmitted 
                ? "border-red-500 focus-visible:ring-red-500" 
                : isFocused 
                  ? "border-blue-500 focus-visible:ring-blue-500" 
                  : "border-gray-300"
            } shadow-sm h-10 xl:h-13 text-sm placeholder:text-sm placeholder:text-gray-400 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-50 group-hover:border-gray-400`}
            placeholder={placeholder}
          />
        </div>
      )}
      
      {!isValid && formSubmitted && (
        <p className="text-red-500 text-xs mt-2 flex items-center">
          <span className="inline-block mr-1">•</span>
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default FormField; 